import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SpareSpending.css';

const API_URL = 'https://dankula.x10.mx/auth.php';

const SpareSpending = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    amount: '',
    category: 'purchase',
    reason: '',
    comment: '',
    payment_method: 'cash',
    bank_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBankSelect, setShowBankSelect] = useState(false);

  const categories = [
    { value: 'purchase', label: 'Purchase' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'consumption', label: 'Consumption' },
    { value: 'institutional', label: 'Institutional' }
  ];

  const institutionalReasons = [
    { value: 'rent', label: 'Rent' },
    { value: 'equb', label: 'Equb' },
    { value: 'accountant', label: 'Accountant' },
    { value: 'salary', label: 'Salary' },
    { value: 'benzene', label: 'Benzene' },
    { value: 'vat15', label: 'VAT 15%' },
    { value: 'betsi_salary', label: 'Betsi Salary' }
  ];

  const banks = ['CBE', 'Awash', 'Dashen', 'Abyssinia', 'Birhan', 'Telebirr'];

  useEffect(() => {
    setShowBankSelect(formData.payment_method === 'bank');
    
    // Reset reason when category changes from institutional to something else
    if (formData.category !== 'institutional' && formData.reason) {
      setFormData(prev => ({ ...prev, reason: '' }));
    }
  }, [formData.payment_method, formData.category]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.reason.trim()) {
      toast.error('Please enter a reason for the spending');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      };

      const spendingData = {
        user_id: user?.id,
        amount: parseFloat(formData.amount),
        category: formData.category,
        reason: formData.reason,
        comment: formData.comment,
        payment_method: formData.payment_method,
        bank_name: formData.payment_method === 'bank' ? formData.bank_name : null
      };

      const response = await axios.post(
        API_URL,
        { ...spendingData, action: 'add_spending' },
        { headers }
      );

      if (response.data.success) {
        toast.success('Spending recorded successfully!');
        // Reset form
        setFormData({
          amount: '',
          category: 'purchase',
          reason: '',
          comment: '',
          payment_method: 'cash',
          bank_name: ''
        });
      } else {
        throw new Error(response.data.message || 'Failed to record spending');
      }
    } catch (err) {
      console.error('Error recording spending:', err);
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="spare-spending-container">
      <div className="spare-spending-card">
        <h2 className="spare-spending-title">Record Spare Parts Spending</h2>
        
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="amount">Amount</label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-money-bill-wave"></i>
              </span>
              <input
                type="number"
                className="form-control"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              className="form-select"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Payment Method</label>
            <div className="payment-methods">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="payment_method"
                  id="cash"
                  value="cash"
                  checked={formData.payment_method === 'cash'}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="cash">
                  <i className="fas fa-money-bill-wave me-1"></i> Cash
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="payment_method"
                  id="bank"
                  value="bank"
                  checked={formData.payment_method === 'bank'}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="bank">
                  <i className="fas fa-university me-1"></i> Bank Transfer
                </label>
              </div>
            </div>
          </div>

          {showBankSelect && (
            <div className="form-group">
              <label htmlFor="bank_name">Select Bank</label>
              <div className="bank-buttons">
                {banks.map(bank => (
                  <button
                    key={bank}
                    type="button"
                    className={`btn btn-outline-primary btn-sm me-2 mb-2 ${formData.bank_name === bank ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, bank_name: bank }))}
                  >
                    {bank}
                  </button>
                ))}
              </div>
              {!formData.bank_name && formData.payment_method === 'bank' && (
                <div className="text-danger small">Please select a bank</div>
              )}
            </div>
          )}

          {formData.category === 'institutional' ? (
            <div className="form-group">
              <label htmlFor="institutional_reason">Institutional Reason</label>
              <select
                className="form-select"
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                required
              >
                <option value="">Select a reason</option>
                {institutionalReasons.map(reason => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="reason">Reason</label>
              <input
                type="text"
                className="form-control"
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Enter reason for spending"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="comment">Comments (Optional)</label>
            <textarea
              className="form-control"
              id="comment"
              name="comment"
              rows="3"
              value={formData.comment}
              onChange={handleChange}
              placeholder="Any additional notes..."
            ></textarea>
          </div>

          <div className="d-grid gap-2 mt-4">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || !formData.amount || parseFloat(formData.amount) <= 0 || !formData.reason.trim() || (formData.payment_method === 'bank' && !formData.bank_name)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Recording...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Record Spending
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpareSpending;
